import {Badge} from "../../../basic/badge";
import {Button} from "../../../basic/button";
import {LicenseSelector} from "../../../shared/selectors/LicenseSelector.tsx";
import {User, UserMinus, UserPlus} from "lucide-react";
import {useTranslationWithKeys} from "../../../../hooks/useTranslationWithKeys";
import {getDisplayName} from "../../../../utils/nameUtils.ts";
import {VwpLicense, VwpTeamMember} from "../../../../store/generated/api";
import LicenseChip from "../../../shared/selectors/LicenseChip";

interface TeamMemberItemProps {
    member: VwpTeamMember;
    availableLicenses: VwpLicense[];
    isManagingMember: boolean;
    isAssigningLicense?: boolean;
    isRemovingLicense?: boolean;
    onToggleManageMember: () => void;
    onAssignLicense: (licenseId: string, licenseName: string, memberEmail: string, memberName: string) => void;
    onRemoveLicense: (licenseId: string, licenseName: string, memberEmail: string, memberName: string) => void;
    onRemoveMember: (memberEmail: string, memberName: string) => void;
}

export function TeamMemberItem({
                                   member,
                                   availableLicenses,
                                   isManagingMember,
                                   isAssigningLicense = false,
                                   isRemovingLicense = false,
                                   onToggleManageMember,
                                   onAssignLicense,
                                   onRemoveLicense,
                                   onRemoveMember
                               }: TeamMemberItemProps) {
    const {t} = useTranslationWithKeys();

    return (
        <div key={member.id} className="p-4 rounded-lg border bg-muted/30">
            <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground"/>
                        <p>{getDisplayName(member)}</p>
                        <Badge variant={member.role === "Admin" ? "default" : "outline"}>
                            {t(`common.role.${member.role.toLowerCase()}`)}
                        </Badge>
                    </div>
                    <p className="text-muted-foreground">{member.email}</p>

                    <div className="space-y-2">
                        <p className="text-muted-foreground">{t('admin.teams.teamManagement.assignedLicenses')}:</p>
                        {member.licenses && member.licenses.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                                {member.licenses.map((license, idx) => (
                                    <LicenseChip
                                        key={idx}
                                        productName={license.name}
                                        licenseId={license.productId}
                                        variant="normal"
                                        onRemove={() => onRemoveLicense(license.productId, license.name, member.email, getDisplayName(member))}
                                    />
                                ))}
                            </div>
                        ) : (
                            <p className="text-muted-foreground">{t('admin.teams.teamManagement.noLicenses')}</p>
                        )}
                    </div>

                    {isManagingMember && (
                        <div className="pt-2 space-y-2">
                            <LicenseSelector
                                value=""
                                onValueChange={(value) => {
                                    const selectedLicense = availableLicenses.find(license => license.id === value);
                                    if (selectedLicense) {
                                        onAssignLicense(value, selectedLicense.product, member.email, getDisplayName(member));
                                    }
                                }}
                                licenses={availableLicenses}
                                placeholder={t('admin.teams.teamManagement.selectLicenseToAssign')}
                                showAvailableCount={true}
                                disabled={isAssigningLicense}
                                actionButton={{
                                    text: t('common.cancel'),
                                    onClick: onToggleManageMember,
                                    variant: "ghost"
                                }}
                            />
                        </div>
                    )}
                </div>

                <div className="flex flex-col gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={onToggleManageMember}
                    >
                        <UserPlus className="h-4 w-4 mr-2"/>
                        {t('admin.teams.teamManagement.assignLicense')}
                    </Button>
                    <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => onRemoveMember(member.email, getDisplayName(member))}
                    >
                        <UserMinus className="h-4 w-4 mr-2"/>
                        {t('common.remove')}
                    </Button>
                </div>
            </div>
        </div>
    );
}